import { createColumnHelper } from '@tanstack/react-table';
import { EstimatedTimeType, Settlement, SettlementQueueItemAction } from '@mews-commander/data-access';
import { LocalizedDateTimeFromUtc, TooltippedIcon } from '@mews-commander/ui';
import { IconName, Typography, Link, Stack } from '@optimus-web/core';
import { WithAccountId } from '@mews-commander/types';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { PREAUTHORIZATION_TYPE } from '../../../constants';
import { showReservationDetail } from '../../../../routes';
import { ReservationTab } from '../../../../../types/reservationTab';
import { SettlementsActionsMenu } from './SettlementsActionsMenu';

const SettlementReservationLink = ({
    reservationId,
    reservationNumber,
}: {
    reservationId: string;
    reservationNumber: string;
}) => {
    const dispatch = useCommanderThunkDispatch();

    const openReservationModal = () =>
        dispatch(showReservationDetail({ reservationId, reservationTab: ReservationTab.Status }));

    return (
        <Link inherit onClick={openReservationModal}>
            {reservationNumber}
        </Link>
    );
};

const columnHelper = createColumnHelper<Settlement>();

export const getSettlementsTableColumns = ({ accountId }: WithAccountId) => [
    columnHelper.accessor('reservationNumber', {
        header: () => <LocalizedText textKey={TextKey.ReservationNumber} />,
        cell: ({
            row: {
                original: { reservationNumber, reservationId },
            },
        }) => <SettlementReservationLink reservationId={reservationId} reservationNumber={reservationNumber} />,
    }),
    columnHelper.accessor('rule', {
        header: () => <LocalizedText textKey={TextKey.Rule} />,
        cell: ({
            row: {
                original: { rule, isVccPayment, date },
            },
        }) => (
            <Stack itemAlignment="center" spacing="100">
                {isVccPayment && (
                    <TooltippedIcon
                        size="small"
                        value={IconName.Info}
                        tooltip={
                            date.discriminator === EstimatedTimeType.ActivationDateUtc
                                ? TextKey.ScheduledSettlementVCPaymentNotApplicable
                                : TextKey.ScheduledSettlementVCPaymentFirstRule
                        }
                    />
                )}
                <Typography textStyle="bodyMediumStrong" as="div">
                    {rule}
                </Typography>
            </Stack>
        ),
    }),
    columnHelper.accessor('action', {
        header: () => <LocalizedText textKey={TextKey.Type} />,
        cell: (props) => {
            switch (props.getValue()) {
                case SettlementQueueItemAction.Payment:
                    return <LocalizedText textKey={TextKey.Payment} />;
                case SettlementQueueItemAction.Preauthorization:
                    return <LocalizedText textKey={TextKey.Preauthorization} />;
                default:
                    return null;
            }
        },
    }),
    columnHelper.accessor('date', {
        header: () => (
            <Stack itemAlignment="center" spacing="100">
                <LocalizedText textKey={TextKey.ScheduledSettlementEstimatedTime} />
                <TooltippedIcon
                    size="small"
                    value={IconName.Info}
                    tooltip={TextKey.ScheduledSettlementEstimatedTimeTooltip}
                />
            </Stack>
        ),
        cell: (props) => {
            const date = props.getValue();
            return (
                <Stack itemAlignment="center" spacing="100">
                    {date.discriminator === EstimatedTimeType.ActivationDateUtc && (
                        <TooltippedIcon
                            size="small"
                            value={IconName.Info}
                            tooltip={TextKey.ScheduledSettlementVCPaymentEstimatedTime}
                        />
                    )}
                    <LocalizedDateTimeFromUtc utcString={date.value} />
                </Stack>
            );
        },
    }),

    columnHelper.accessor('action', {
        header: () => <LocalizedText textKey={TextKey.ActionPlural} />,
        meta: {
            align: 'end',
        },
        cell: ({
            row: {
                original: { id, action },
            },
        }) => (
            <SettlementsActionsMenu
                accountId={accountId}
                settlementId={id}
                isPreauthorization={action === PREAUTHORIZATION_TYPE}
            />
        ),
    }),
];
